<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import { api, type AlertOverlay, type PlayerConfig } from './api';
  import AlertOverlayView from './AlertOverlay.svelte';
  import Carousel from './Carousel.svelte';
  import HdmiMode from './HdmiMode.svelte';
  import { startHeartbeat } from './heartbeat';
  import { precacheMedia } from './media-cache';
  import { startPlaybackTracker, stopPlaybackTracker } from './playback-tracker';
  import { loadConfig, saveConfig, saveContentVersion } from './storage';
  import { connect, type PlayerSocket } from './socket';

  let config: PlayerConfig | null = null;
  let alert: AlertOverlay | null = null;
  let socket: PlayerSocket | null = null;
  let stopHb: (() => void) | null = null;
  let connectionState: 'connecting' | 'connected' | 'disconnected' = 'connecting';
  const recentErrors: string[] = [];

  onMount(async () => {
    startPlaybackTracker();

    // Cargar config inicial
    await refreshConfig();

    // Conectar WS
    try {
      socket = connect();
      bindSocket(socket);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Socket connect failed:', err);
    }

    // Empezar heartbeat (solo si tenemos socket)
    if (socket) {
      stopHb = startHeartbeat(socket, () => ({
        contentVersion: config?.contentVersion ?? null,
        errors: recentErrors.slice(-10),
      }));
    }
  });

  onDestroy(() => {
    stopHb?.();
    stopPlaybackTracker();
    socket?.close();
  });

  async function refreshConfig() {
    try {
      const next = await api.getConfig();
      config = next;
      alert = next?.alert ?? null;
      if (next?.contentVersion) saveContentVersion(next.contentVersion);
      saveConfig(next); // persistir la última config OK para sobrevivir cortes

      // Pre-cache los primeros 5 medios de la playlist activa
      if (next?.active?.type === 'PLAYLIST') {
        const urls = next.active.playlist.items.slice(0, 5).map((i) => i.mediaAsset.cdnUrl);
        void precacheMedia(urls);
      }
    } catch (err) {
      pushError(`config fetch: ${(err as Error).message}`);
      // Sin config en memoria (ej. reinicio con el server/red caídos): hidratar
      // desde disco para NO quedar en negro. Los medios ya están en el cache del SW.
      if (!config) {
        const cached = loadConfig();
        if (cached) {
          config = cached;
          alert = cached.alert ?? null;
          if (cached.active?.type === 'PLAYLIST') {
            const urls = cached.active.playlist.items.slice(0, 5).map((i) => i.mediaAsset.cdnUrl);
            void precacheMedia(urls);
          }
        }
      }
    }
  }

  function bindSocket(s: PlayerSocket) {
    s.on('connect' as never, () => {
      connectionState = 'connected';
    });
    s.on('disconnect' as never, () => {
      connectionState = 'disconnected';
    });
    s.on('reconnect' as never, () => {
      connectionState = 'connected';
      void refreshConfig();
    });

    s.on('server:reload-config', () => {
      void refreshConfig();
    });

    s.on('server:show-alert', (...args: unknown[]) => {
      const payload = args[0] as AlertOverlay;
      alert = payload;
    });

    s.on('server:dismiss-alert', () => {
      alert = null;
    });

    s.on('server:start-takeover', () => {
      void refreshConfig();
    });

    s.on('server:end-takeover', () => {
      void refreshConfig();
    });

    s.on('server:invalidate-media', () => {
      // El SW limpiará el cache; aquí solo refrescamos config
      void refreshConfig();
    });

    s.on('server:hardware-command', (...args: unknown[]) => {
      const payload = args[0] as {
        command: string;
        args?: { input?: string; volume?: number; brightness?: number };
      };
      handleHardwareCommand(payload);
    });

    s.on('server:auth-error', () => {
      // Token inválido — purgar y reiniciar app
      // eslint-disable-next-line no-console
      console.error('Auth error — clearing token, reload required');
      localStorage.removeItem('signage:auth-token');
      window.location.reload();
    });
  }

  function handleHardwareCommand(payload: {
    command: string;
    args?: { input?: string; volume?: number; brightness?: number };
  }) {
    // Acá se hookean las Tizen TV APIs reales.
    // En MVP, solo logueamos.
    // eslint-disable-next-line no-console
    console.log('Hardware command:', payload);

    // Intento real con tizen API si está disponible
    const w = window as unknown as Record<string, unknown>;
    const tizen = w['tizen'] as
      | { tvinputdevice?: { setSource?: (s: string) => void } }
      | undefined;
    if (tizen?.tvinputdevice?.setSource && payload.command === 'set-input' && payload.args?.input) {
      try {
        tizen.tvinputdevice.setSource(payload.args.input);
      } catch (err) {
        pushError(`tizen.setSource: ${(err as Error).message}`);
      }
    }
  }

  function pushError(msg: string) {
    recentErrors.push(`${new Date().toISOString()} ${msg}`);
    if (recentErrors.length > 50) recentErrors.shift();
    // eslint-disable-next-line no-console
    console.warn('[player error]', msg);
  }
</script>

{#if config?.active?.type === 'HDMI_INPUT'}
  <HdmiMode input={config.active.hdmiInput} />
{:else if config?.active?.type === 'PLAYLIST' && config.active.playlist.items.length > 0}
  <Carousel playlist={config.active.playlist} />
{:else}
  <div class="empty">
    <p>Sin contenido programado</p>
    <small>{config?.screenId ?? '…'}</small>
  </div>
{/if}

{#if alert}
  <AlertOverlayView {alert} />
{/if}

{#if connectionState !== 'connected'}
  <div class="conn-state" class:offline={connectionState === 'disconnected'}>
    {connectionState === 'connecting' ? 'Conectando…' : 'Sin conexión — reproduciendo cacheado'}
  </div>
{/if}

<style>
  .empty {
    position: absolute;
    inset: 0;
    background: #000;
    color: #444;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }
  .empty p { font-size: 2rem; }
  .empty small { font-family: monospace; opacity: 0.4; }
  .conn-state {
    position: absolute;
    top: 1rem;
    right: 1rem;
    padding: 0.5rem 1rem;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    backdrop-filter: blur(10px);
  }
  .conn-state.offline {
    background: rgba(220, 38, 38, 0.85);
  }
</style>
